import React from 'react'

import cx from 'classnames'
import { matchPath, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { useCompactNavbarVisible, useWindowWidth } from '@hooks'

import { ServiceTypeLogo } from '@components/logos'
import { NavbarSupportLauncherButton, SupportChannelRoot } from '@components/Support'
import { AppBar, ClickAwayListener, FlexRow, IconButton } from '@components/ui'
import { MenuIcon } from '@components/ui/svgIcons'

import {
  generateMaxHeightBreakPoint,
  generateMaxWidthBreakPoint,
  generateMinWidthBreakPoint,
  RouteKeys,
  ROUTES,
} from '@constants'

import { BackToSzamlazzButton, LogoutButton, MobileProfileMenuToggleButton, ProfileMenu } from '../elements'
import { MainMenu } from './elements'

import { StyledNavLink, StyledToolbar } from './styles'

const ProfileMenuContainer = styled(FlexRow)`
  ${generateMaxWidthBreakPoint('small')} {
    flex-direction: column;
  }
`

const MobileMenuContainerDiv = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const LogoLink = styled(StyledNavLink)`
  margin-right: 8px;

  ${generateMinWidthBreakPoint('large')} {
    margin-right: 1rem;
  }
`

const StyledAppBar = styled(AppBar)`
  &.dense {
    top: calc(
      ${({ theme }) => theme.appBarHeightMedium - theme.appBarHeight}px + ${({ theme }) => theme.newVersionBarOffset}
    );
  }
`

const MobileRoot = styled.div`
  padding-left: 24px;
  padding-right: 24px;
  min-height: ${({ theme }) => theme.appBarHeight}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.gray[0]};

  ${generateMaxHeightBreakPoint('medium')} {
    min-height: ${({ theme }) => theme.appBarHeightMedium}px;
  }
`

const ToolbarInnerContainer = styled.div<{ $isNavigation?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;

  & > div:first-child {
    display: flex;
    align-items: center;
  }

  ${generateMaxHeightBreakPoint('medium')} {
    height: ${({ theme }) => theme.appBarHeightMedium}px;
  }

  ${({ $isNavigation }) =>
    $isNavigation &&
    css`
      padding-left: 0;
      padding-right: 0;
    `}

  ${generateMaxWidthBreakPoint('small')} {
    padding-left: 0;
    padding-right: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.gray[105]};
    flex-direction: column;

    & > div:first-child {
      flex-direction: column;
    }

    & > div:first-child > a,
    & > div:first-child > span {
      &,
      & > span {
        width: 100%;
      }
    }
  }
`

enum MenuVariant {
  COMPANY = 'company',
  NAVIGATION = 'navigation',
}

interface PrivateNavbarProps {
  hasSubscription: boolean
  withoutLinks?: boolean
  withSupportLauncher?: boolean
}

export function PrivateNavbar({
  hasSubscription,
  withoutLinks = false,
  withSupportLauncher = false,
}: PrivateNavbarProps) {
  const [menu, setMenu] = React.useState<Nullable<MenuVariant>>(null)
  const isDesktop = useWindowWidth(1200)
  const location = useLocation()
  const dense = useCompactNavbarVisible()

  function toggleNavigationMenu() {
    setMenu(state => (state === MenuVariant.NAVIGATION ? null : MenuVariant.NAVIGATION))
  }

  function toggleCompanyMenu() {
    setMenu(state => (state === MenuVariant.COMPANY ? null : MenuVariant.COMPANY))
  }

  function handleClose() {
    if (menu) {
      setMenu(null)
    }
  }

  const matchProfile = matchPath(ROUTES[RouteKeys.PROFILE], location.pathname)
  const matchCompany = matchPath(ROUTES[RouteKeys.COMPANY], location.pathname)
  const isActive = Boolean(matchProfile || matchCompany)

  return (
    <>
      <SupportChannelRoot withFloatingLauncher={withSupportLauncher} />
      <StyledAppBar className={cx({ dense })} noShadow={dense}>
        <ClickAwayListener onClickAway={handleClose}>
          <StyledToolbar open={Boolean(menu)}>
            {!isDesktop && (
              <MobileRoot>
                <MobileMenuContainerDiv>
                  <StyledNavLink to={ROUTES.root} activeWhenNoCompany linkWhenNoCompany>
                    <ServiceTypeLogo />
                  </StyledNavLink>
                  {!withoutLinks && (
                    <IconButton onClick={toggleNavigationMenu}>
                      <MenuIcon size={28} />
                    </IconButton>
                  )}
                </MobileMenuContainerDiv>
                <MobileProfileMenuToggleButton open={menu === MenuVariant.COMPANY} onClick={toggleCompanyMenu} />
              </MobileRoot>
            )}
            <ToolbarInnerContainer $isNavigation={menu === MenuVariant.NAVIGATION}>
              <div onClick={handleClose}>
                {isDesktop && (
                  <LogoLink to={ROUTES.root} activeWhenNoCompany linkWhenNoCompany>
                    <ServiceTypeLogo />
                  </LogoLink>
                )}
                {((isDesktop && !withoutLinks) || (!isDesktop && menu === MenuVariant.NAVIGATION)) && (
                  <MainMenu hasSubscription={hasSubscription} />
                )}
              </div>

              {!isDesktop && menu === MenuVariant.COMPANY && (
                <ProfileMenu isActive={isActive} onCloseCallback={handleClose} />
              )}
              {(isDesktop || menu === MenuVariant.NAVIGATION) && (
                <ProfileMenuContainer onClick={handleClose}>
                  {isDesktop && <ProfileMenu isActive={isActive} isDesktop />}
                  {withSupportLauncher && <NavbarSupportLauncherButton showIconButton={isDesktop} />}
                  <BackToSzamlazzButton isDesktop={isDesktop} />
                  <LogoutButton isDesktop={isDesktop} />
                </ProfileMenuContainer>
              )}
            </ToolbarInnerContainer>
          </StyledToolbar>
        </ClickAwayListener>
      </StyledAppBar>
    </>
  )
}
